import moment from "moment";
import Api from "lib/api/httpRequestHelper";
import UtilsFncs from "utils/UtilsFncs";
import MaskAndValid from "utils/maskAndValid";

/** Sentinel value: notify every store matching current filters (not only visible rows). */
export const NOTIFY_ALL_IN_SCOPE_KEY = "__bulk__";

export function parseRequestError(error, fallbackMessage) {
  const fromObject =
    error && typeof error === "object" && error.message
      ? String(error.message)
      : null;
  if (fromObject) return fromObject;
  if (typeof error === "string" && error.trim()) return error;
  return fallbackMessage;
}

function formatCurrencyLabel(value) {
  if (value == null || value === "") return "R$ 0,00";
  const s = String(value).trim();
  if (s.startsWith("R$")) return s;
  return `R$ ${s}`;
}

/** Total (número ou string legada) — soma do valor de títulos financeiros liquidados (LQ) no período, por data de liquidação. */
export function formatSalesTotalLabelFromApiValue(value) {
  if (value == null || value === "") return "R$ 0,00";
  if (typeof value === "number" && Number.isFinite(value)) {
    return `R$ ${MaskAndValid.maskPrice(value)}`;
  }
  return formatCurrencyLabel(value);
}

const BOLETOS_FILTER_SENTINELS = new Set(["", "TODAS", "TODOS"]);

/** Valor de filtro estrutural ativo (exclui vazio e sentinelas "TODAS"/"TODOS"). */
export function isBoletosStructureFilterActive(value) {
  if (value == null) return false;
  const normalized = String(value).trim();
  if (!normalized) return false;
  return !BOLETOS_FILTER_SENTINELS.has(normalized.toUpperCase());
}

function hasBoletosCnpjCriteria(cnpj) {
  const digits = String(cnpj || "").replace(/\D/g, "");
  return digits.length >= 11;
}

/**
 * Critérios aceitos (OR): grupo econômico; grupo + loja (razão social);
 * loja (razão social) isolada; CNPJ (≥11 dígitos).
 */
export function hasBoletosDashboardSearchCriteria(structureFilters, cnpj) {
  const f = structureFilters || {};
  if (isBoletosStructureFilterActive(f.idGrupoEconomico)) return true;
  if (isBoletosStructureFilterActive(f.idLoja)) return true;
  return hasBoletosCnpjCriteria(cnpj);
}

/** Remove filtros estruturais inativos antes de montar query/body da API. */
export function normalizeBoletosDashboardStructureFilters(structureFilters = {}) {
  const normalized = { ...structureFilters };
  if (!isBoletosStructureFilterActive(normalized.idGrupoEconomico)) {
    delete normalized.idGrupoEconomico;
  }
  if (!isBoletosStructureFilterActive(normalized.idLoja)) {
    delete normalized.idLoja;
  }
  if (!isBoletosStructureFilterActive(normalized.idMarca)) {
    delete normalized.idMarca;
  }
  return normalized;
}

function appendBoletosStructureFiltersToParams(
  params,
  structureFilters,
  { useSearchParams = true } = {},
) {
  const filters = normalizeBoletosDashboardStructureFilters(structureFilters);
  const entries = [
    ["idMarca", filters.idMarca],
    ["idGrupoEconomico", filters.idGrupoEconomico],
    ["idLoja", filters.idLoja],
  ];
  entries.forEach(([key, value]) => {
    if (!isBoletosStructureFilterActive(value)) return;
    if (useSearchParams) {
      params.set(key, String(value).trim());
    } else {
      params[key] = String(value).trim();
    }
  });
}

export async function resolveRepresentativeIdFromSession() {
  try {
    const visionRaw = localStorage.getItem("userDetailVision");
    const baseRaw = visionRaw || localStorage.getItem("userDetail");
    if (!baseRaw) return null;
    const userDetail = JSON.parse(baseRaw);
    const directId =
      userDetail?.IdRepresentante ??
      userDetail?.idRepresentante ??
      userDetail?.IdPessoaRepresentante;
    if (directId != null && String(directId).trim() !== "") {
      return String(directId);
    }

    const requestParams = UtilsFncs.RestParamsComposer({});
    const response = await Api.get(
      "representative",
      "commercialstructure",
      requestParams,
      true,
    );
    const list = response?.Result;
    if (!Array.isArray(list) || list.length === 0) return null;
    if (list.length === 1) return String(list[0].Id);
    const userId = userDetail?.IdUsuario;
    const match = list.find(
      (item) =>
        item.IdUsuario === userId ||
        item.IdUsuarioLogin === userId ||
        String(item.IdUsuario) === String(userId),
    );
    if (match) return String(match.Id);
    return String(list[0].Id);
  } catch {
    return null;
  }
}

function buildOpenInvoicesDashboardQueryString({
  isFranquiado,
  structureFilters,
  cnpj,
  representativeId,
}) {
  const params = new URLSearchParams();
  params.set("franquia", isFranquiado ? "true" : "false");
  const today = moment().format("YYYY-MM-DD");
  const plus30 = moment().add(30, "days").format("YYYY-MM-DD");
  const overdueLookbackStart = moment()
    .subtract(30, "days")
    .format("YYYY-MM-DD");
  params.set("dtInicialVencidos", overdueLookbackStart);
  params.set("dtFinalVencidos", today);
  params.set("dtInicialAVencer", today);
  params.set("dtFinalAVencer", plus30);
  appendBoletosStructureFiltersToParams(params, structureFilters);
  const cnpjDigits = (cnpj || "").replace(/\D/g, "");
  if (cnpjDigits.length >= 11) {
    params.set("cnpj", cnpjDigits);
  }
  if (representativeId != null && String(representativeId).trim() !== "") {
    params.set("idRepresentante", String(representativeId).trim());
  }
  return `?${params.toString()}`;
}

function buildOpenInvoicesExportQueryString({
  situationType,
  isFranquiado,
  structureFilters,
  cnpj,
  representativeId,
}) {
  const params = new URLSearchParams();
  params.set("tipo", situationType);
  params.set("franquia", isFranquiado ? "true" : "false");
  const today = moment().format("YYYY-MM-DD");
  const plus30 = moment().add(30, "days").format("YYYY-MM-DD");
  const overdueLookbackStart = moment()
    .subtract(30, "days")
    .format("YYYY-MM-DD");
  params.set("dtInicialVencidos", overdueLookbackStart);
  params.set("dtFinalVencidos", today);
  params.set("dtInicialAVencer", today);
  params.set("dtFinalAVencer", plus30);
  appendBoletosStructureFiltersToParams(params, structureFilters);
  const cnpjDigits = (cnpj || "").replace(/\D/g, "");
  if (cnpjDigits.length >= 11) {
    params.set("cnpj", cnpjDigits);
  }
  if (representativeId != null && String(representativeId).trim() !== "") {
    params.set("idRepresentante", String(representativeId).trim());
  }
  return `?${params.toString()}`;
}

export async function fetchOpenInvoicesExportList({
  situationType,
  isFranquiado,
  structureFilters,
  cnpj,
  representativeId,
}) {
  try {
    const queryString = buildOpenInvoicesExportQueryString({
      situationType,
      isFranquiado,
      structureFilters: structureFilters || {},
      cnpj: cnpj || "",
      representativeId,
    });
    const response = await Api.get(
      "export-home-financial-boletos-list",
      "financialdocument",
      queryString,
      true,
    );
    const validationMessage =
      response?.ValidationMessage ?? response?.validationMessage;
    if (validationMessage) {
      throw new Error(validationMessage);
    }
    if (response?.Success === false) {
      throw new Error(
        response?.ErrorMessage ??
          response?.errorMessage ??
          "Erro ao exportar boletos.",
      );
    }
    const rows = response?.Result ?? response?.result ?? [];
    return Array.isArray(rows) ? rows : [];
  } catch (error) {
    throw new Error(
      parseRequestError(error, "Erro ao exportar lista de boletos."),
    );
  }
}

export async function fetchOpenInvoicesDashboard({
  isFranquiado,
  structureFilters,
  cnpj,
  representativeId,
}) {
  try {
    const queryString = buildOpenInvoicesDashboardQueryString({
      isFranquiado,
      structureFilters: structureFilters || {},
      cnpj: cnpj || "",
      representativeId,
    });
    const response = await Api.get(
      "get-home-financial-boletos-dashboard",
      "financialdocument",
      queryString,
      true,
    );
    const validationMessage =
      response?.ValidationMessage ?? response?.validationMessage;
    if (validationMessage) {
      throw new Error(validationMessage);
    }
    if (response?.Success === false) {
      throw new Error(
        response?.ErrorMessage ??
          response?.errorMessage ??
          "Erro ao carregar dados.",
      );
    }
    return response?.Result ?? response?.result ?? null;
  } catch (error) {
    throw new Error(
      parseRequestError(error, "Erro ao carregar boletos em aberto."),
    );
  }
}

function buildNotificationDateRangePayload() {
  const today = moment().format("YYYY-MM-DD");
  const plus30 = moment().add(30, "days").format("YYYY-MM-DD");
  return {
    dtInicialVencidos: "1990-01-01",
    dtFinalVencidos: today,
    dtInicialAVencer: today,
    dtFinalAVencer: plus30,
  };
}

export async function postInvoiceClientNotifications({
  situationType,
  isFranquiado,
  representativeId,
  committedFilters,
  committedCnpj,
  destinations,
  notifyAllInScope,
}) {
  const body = {
    tipo: situationType,
    franquia: isFranquiado ? "true" : "false",
    ...buildNotificationDateRangePayload(),
  };
  if (notifyAllInScope) {
    body.notificarTodosNoEscopo = true;
    body.destinos = [];
  } else {
    body.destinos = destinations;
  }
  if (representativeId != null && String(representativeId).trim() !== "") {
    body.idRepresentante = String(representativeId).trim();
  }
  appendBoletosStructureFiltersToParams(body, committedFilters, {
    useSearchParams: false,
  });
  const cnpjDigits = (committedCnpj || "").replace(/\D/g, "");
  if (cnpjDigits.length >= 11) {
    body.cnpj = cnpjDigits;
  }
  try {
    const response = await Api.post(
      "notify-boletos-clientes",
      "financialdocument",
      body,
      true,
    );
    const validationMessage =
      response?.ValidationMessage ?? response?.validationMessage;
    if (validationMessage) {
      throw new Error(validationMessage);
    }
    if (response?.Success === false) {
      throw new Error(
        response?.ErrorMessage ??
          response?.errorMessage ??
          "Falha ao notificar.",
      );
    }
    return response?.Result ?? response?.result ?? {};
  } catch (error) {
    throw new Error(parseRequestError(error, "Erro ao enviar notificações."));
  }
}

export function getInvoiceRowActionKey(row, rowIndex) {
  if (row?.IdTitulo != null && String(row.IdTitulo).trim() !== "") {
    return String(row.IdTitulo);
  }
  const invoiceNumber = row?.NumeroTitulo ?? row?.numeroTitulo ?? "t";
  return `${invoiceNumber}-${rowIndex}`;
}

export function getInvoiceDueMoment(row) {
  const raw = !MaskAndValid.isNullOrEmpty(row?.DtVencimentoProrrogado)
    ? row.DtVencimentoProrrogado
    : row?.DtVencimento;
  return raw ? moment(raw) : null;
}

export function formatDisplayDate(value) {
  if (!value) return "";
  if (
    typeof value === "string" &&
    value.length === 10 &&
    /^\d{4}-\d{2}-\d{2}$/.test(value)
  ) {
    if (!MaskAndValid.validateDate(value)) return "";
    return `${value.substring(8, 10)}/${value.substring(5, 7)}/${value.substring(0, 4)}`;
  }
  return MaskAndValid.formattDateBR(value) || "";
}

export function buildTitlesTrackingReportPath(
  situationPreset,
  structureFilters,
  extra = {},
) {
  const params = new URLSearchParams();
  params.set("presetSituacao", situationPreset);
  params.set("tipoData", "DATA_VENCIMENTO");
  const today = moment().format("YYYY-MM-DD");
  const plus30 = moment().add(30, "days").format("YYYY-MM-DD");
  if (situationPreset === "VENCIDOS") {
    const overdueLookbackStart = moment()
      .subtract(30, "days")
      .format("YYYY-MM-DD");
    params.set("dtInicial", overdueLookbackStart);
    params.set("dtFinal", today);
  } else {
    params.set("dtInicial", today);
    params.set("dtFinal", plus30);
  }
  appendBoletosStructureFiltersToParams(params, structureFilters);
  const representativeId = extra?.representativeId;
  if (
    representativeId != null &&
    String(representativeId).trim() !== ""
  ) {
    params.set("idRepresentante", String(representativeId).trim());
  }
  return `/admin/relatorio-acompanhamento-titulos?${params.toString()}`;
}

export function normalizeInvoiceRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const openAmount = row?.ValorEmAberto ?? row?.valorEmAberto;
    const faceValue = row?.Valor ?? row?.valor;
    const openNumeric = openAmount != null ? Number(openAmount) : 0;
    const idTitulo = row?.IdTitulo ?? row?.Id ?? row?.id;
    const podeRaw =
      row?.PodeNotificarCliente ?? row?.podeNotificarCliente ?? undefined;
    const podeNotificarCliente =
      podeRaw === true || podeRaw === false ? podeRaw : true;
    const motivoBloqueio =
      row?.MotivoBloqueioNotificacaoCliente ??
      row?.motivoBloqueioNotificacaoCliente ??
      "";
    return {
      ...row,
      IdTitulo: idTitulo,
      Valor: openNumeric > 0 ? openAmount : faceValue,
      PodeNotificarCliente: podeNotificarCliente,
      MotivoBloqueioNotificacaoCliente: motivoBloqueio,
    };
  });
}

function filterOpenStatusRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter(
    (row) => row?.Situacao === "AB" || row?.situacao === "AB",
  );
}

function sortRowsByDueDateAscending(rows) {
  return [...rows].sort((a, b) => {
    const dueA = getInvoiceDueMoment(a);
    const dueB = getInvoiceDueMoment(b);
    if (!dueA && !dueB) return 0;
    if (!dueA) return 1;
    if (!dueB) return -1;
    return dueA.valueOf() - dueB.valueOf();
  });
}

export function getOverdueDayCount(row) {
  const due = getInvoiceDueMoment(row);
  if (!due) return 0;
  return Math.max(0, moment().startOf("day").diff(due.startOf("day"), "days"));
}

export function buildOverdueRowsForDisplay(rows) {
  return sortRowsByDueDateAscending(filterOpenStatusRows(rows));
}

export function buildUpcomingRowsForDisplay(rows) {
  const rangeStart = moment().startOf("day");
  const rangeEnd = moment().add(30, "days").endOf("day");
  const filtered = filterOpenStatusRows(rows).filter((row) => {
    const due = getInvoiceDueMoment(row);
    if (!due) return false;
    return due.isSameOrAfter(rangeStart) && due.isSameOrBefore(rangeEnd);
  });
  return sortRowsByDueDateAscending(filtered);
}
